import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { rand, monthLabel } from '@/components/stokvel/format';

const MoneyInput = ({ value, onSave, testid }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const blur = () => {
    const nv = v === '' ? null : Number(v);
    if (nv === (value ?? null)) return;
    onSave(nv);
  };
  return <Input className="h-8 w-28 ml-auto text-right" type="number" step="0.01" value={v} onChange={e => setV(e.target.value)} onBlur={blur} data-testid={testid} />;
};

const Card = ({ label, value, testid, tone }) => (
  <div className={`rounded-lg border p-3 ${tone || 'border-stone-200'}`}>
    <div className="text-[11px] uppercase tracking-wide text-stone-500">{label}</div>
    <div className="text-lg font-bold text-stone-800" data-testid={testid}>{value}</div>
  </div>
);

export const StokvelReconPanel = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/stokvel/admin/recon`, { params: { year } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load recon'); }
  }, [year]);
  useEffect(() => { load(); }, [load]);
  const saveMonth = async (ym, field, value) => {
    try { await axios.put(`${API}/stokvel/admin/months/${ym}`, { [field]: value }); toast.success(`${monthLabel(ym)} ${field === 'interest' ? 'interest' : 'bank balance'} saved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  if (!d) return <p className="text-sm text-stone-500">Loading…</p>;
  const tot = (k) => d.pool.reduce((s, r) => s + (r[k] || 0), 0);
  const ok = Math.abs(d.check.unexplained) < 0.005;
  return (
    <div className="space-y-5" data-testid="stokvel-recon-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Cycle year</Label><Input type="number" className="w-28" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="stokvel-recon-year" /></div>
        <p className="text-xs text-stone-500 max-w-xl">Enter the interest the bank paid each month — it is split by each member's capital balance at month end (to the cent). Enter the bank statement closing balance to see the variance.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card label="Expected bank balance" value={rand(d.check.expected_bank_balance)} testid="stokvel-recon-expected" />
        <Card label="Sum of member pots" value={rand(d.check.sum_member_payouts)} testid="stokvel-recon-members" />
        <Card label="Penalty pot (cycle)" value={rand(d.penalty_pot)} testid="stokvel-recon-pot" />
        <Card label="Undistributed (pot + interest)" value={rand(d.check.explained_by_undistributed)} testid="stokvel-recon-undistributed" />
        <Card label="Unexplained difference" value={rand(d.check.unexplained)} testid="stokvel-recon-unexplained"
          tone={ok ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'} />
      </div>
      <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-800' : 'text-rose-800'}`}>
        {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {ok ? 'Ledger balances: every rand in the account is allocated to a member or the penalty pot.' : 'Ledger does not balance — check recent edits.'}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="stokvel-recon-table">
          <thead className="bg-stone-100 text-xs uppercase text-stone-600">
            <tr>
              <th className="text-left px-2 py-2">Month</th><th className="text-right px-2 py-2">+ Deductions</th><th className="text-right px-2 py-2">+ Company</th>
              <th className="text-right px-2 py-2">+ Interest (enter)</th><th className="text-right px-2 py-2">− Withdrawals</th><th className="text-right px-2 py-2">− Exit refunds</th>
              <th className="text-right px-2 py-2">Penalties → pot</th><th className="text-right px-2 py-2">= Closing</th>
              <th className="text-right px-2 py-2">Bank statement (enter)</th><th className="text-right px-2 py-2">Variance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {d.pool.map(r => (
              <tr key={r.month} data-testid={`stokvel-recon-row-${r.month}`}>
                <td className="px-2 py-1.5 font-medium">{monthLabel(r.month)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{rand(r.contribution)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{rand(r.company)}</td>
                <td className="px-2 py-1.5"><MoneyInput value={r.interest || null} onSave={v => saveMonth(r.month, 'interest', v)} testid={`stokvel-interest-${r.month}`} /></td>
                <td className="px-2 py-1.5 text-right font-mono">{rand(r.withdrawal)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{rand(r.refund)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-stone-500">{rand(r.penalty)}</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold" data-testid={`stokvel-closing-${r.month}`}>{rand(r.closing)}</td>
                <td className="px-2 py-1.5"><MoneyInput value={r.bank_balance} onSave={v => saveMonth(r.month, 'bank_balance', v)} testid={`stokvel-bank-${r.month}`} /></td>
                <td className={`px-2 py-1.5 text-right font-mono ${r.variance ? 'text-rose-700 font-semibold' : 'text-emerald-700'}`} data-testid={`stokvel-variance-${r.month}`}>
                  {r.variance === null || r.variance === undefined ? '—' : rand(r.variance)}
                </td>
              </tr>
            ))}
            <tr className="bg-stone-50 font-semibold">
              <td className="px-2 py-2">Total</td>
              {['contribution', 'company', 'interest', 'withdrawal', 'refund', 'penalty'].map(k => <td key={k} className="px-2 py-2 text-right font-mono">{rand(tot(k))}</td>)}
              <td className="px-2 py-2 text-right font-mono">{rand(d.check.expected_bank_balance)}</td><td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-stone-500">Penalty pot = exit penalty fees + interest and company contributions forfeited on resignation/dismissal. It is shared pro rata to remaining active members' capital contributions for the cycle (shown as "Penalty pot share" on the Members tab).</p>
    </div>
  );
};
